import React, { useEffect, useState } from 'react';

import { Card, Spin } from 'antd-v5';
import { get } from 'lodash';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Button } from 'scouting-ui/lib';

import {
  useDeleteMBFileMutation,
  useGetImportFileStatusQuery,
  useStartMBFileProcessingMutation,
} from '@modules/councilUnits/api';
import {
  COUNCIL_IMPORT_FILE_POLL_INTERVAL,
  importFileStatus,
} from '@modules/councilUnits/constants';
import { isCouncilUsingUnitSel } from '@modules/councilUnits/duck';
import { DetailItem } from '@shared/components';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';
import { toastService } from '@toasts';

import styles from './CouncilImportFileStatus.less';

function CouncilmportFileStatus() {
  const orgGUID = useSelector(organizationGuidSel);
  const isCouncilUsingUnit = useSelector(isCouncilUsingUnitSel);
  const [pollingInterval, setPollingInterval] = useState(
    COUNCIL_IMPORT_FILE_POLL_INTERVAL,
  );

  const {
    isFetching: fetchingImportFile,
    isLoading: loadingImportFile,
    data: statusData,
    isError,
  } = useGetImportFileStatusQuery(
    { orgGUID },
    { skip: !orgGUID || isCouncilUsingUnit, pollingInterval },
  );

  const fileName = statusData?.fileName || '';
  const processStatus = statusData?.processStatus || '';
  const processingMsg = statusData?.processingMsg || '';
  const uploadId = statusData?.id || 0;

  const summarydata = statusData?.summaryReport
    ? JSON.parse(statusData.summaryReport)
    : '';
  const [processFileMB, { isLoading: isloadingStartMB }] =
    useStartMBFileProcessingMutation();

  const [deleteFileMB, { isLoading: isloadingDeleteMB }] =
    useDeleteMBFileMutation();
  const stopPolling =
    processStatus == importFileStatus.PROCESSING_DONE || isError;

  useEffect(() => {
    if (stopPolling) setPollingInterval(0);
  }, [stopPolling]);

  const loading =
    loadingImportFile ||
    fetchingImportFile ||
    isloadingStartMB ||
    isloadingDeleteMB;

  const handleProcessMBFile = async () => {
    try {
      await processFileMB({
        orgGUID,
        importFileID: uploadId,
      }).unwrap();

      toastService.success(
        <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.processMessage" />,
      );
    } catch (error) {
      const errorMessage = get(error, ['data', 'errorDesc']);
      if (errorMessage) {
        toastService.error(errorMessage);
      }
    }
  };

  const handleDeleteMBFile = async () => {
    try {
      await deleteFileMB({
        orgGUID,
        importFileID: uploadId,
      }).unwrap();

      toastService.success(
        <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.deleteMessage" />,
      );
    } catch (error) {
      const errorMessage = get(error, ['data', 'errorDesc']);
      if (errorMessage) {
        toastService.error(errorMessage);
      }
    }
  };

  const renderDisplay = () => {
    switch (processStatus) {
      case importFileStatus.IN_QUEUE:
        return (
          <div>
            <Card>
              <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Step1" />
              <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Step1Message" />

              {fileName && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.FileName" />
                  }
                >
                  {fileName}
                </DetailItem>
              )}
              {processStatus && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.ImportStatus" />
                  }
                >
                  {processStatus}
                </DetailItem>
              )}
              {processingMsg && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.ProcessingMessage" />
                  }
                >
                  {processingMsg}
                </DetailItem>
              )}
              {uploadId && (
                <Button
                  size="small"
                  type="default"
                  className={styles.actionButton}
                  onClick={handleDeleteMBFile}
                >
                  <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.CancelUpload" />
                </Button>
              )}
            </Card>
          </div>
        );
      case importFileStatus.VALIDATING:
        return (
          <div>
            <Card>
              <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Step2" />
              <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Step2Message" />
              {fileName && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.FileName" />
                  }
                >
                  {fileName}
                </DetailItem>
              )}
              {processStatus && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.ImportStatus" />
                  }
                >
                  {processStatus}
                </DetailItem>
              )}
              {processingMsg && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.ProcessingMessage" />
                  }
                >
                  {processingMsg}
                </DetailItem>
              )}
              {uploadId && (
                <Button
                  size="small"
                  type="default"
                  className={styles.actionButton}
                  onClick={handleDeleteMBFile}
                >
                  <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.CancelUpload" />
                </Button>
              )}
            </Card>
          </div>
        );
      case importFileStatus.VALIDATION_DONE:
        return (
          <div>
            <Card>
              <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Step3" />
              <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Step3Message" />
              {summarydata.UploadDate && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Summary.UploadDate" />
                  }
                >
                  {summarydata.UploadDate}
                </DetailItem>
              )}
              {summarydata.CouncilAdmin && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Summary.CouncilAdmin" />
                  }
                >
                  {summarydata.CouncilAdmin}
                </DetailItem>
              )}
              {summarydata.RowCount && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Summary.RowCount" />
                  }
                >
                  {summarydata.RowCount}
                </DetailItem>
              )}
              {summarydata.RowWithErrors && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Summary.RowWithErrors" />
                  }
                >
                  {summarydata.RowWithErrors}
                </DetailItem>
              )}
              {summarydata.RowForProcess && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Summary.RowForProcess" />
                  }
                >
                  {summarydata.RowForProcess}
                </DetailItem>
              )}
              {uploadId && (
                <div>
                  <Button
                    size="small"
                    type="default"
                    className={styles.actionButton}
                    onClick={handleProcessMBFile}
                  >
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.ProcessFile" />
                  </Button>
                  <Button
                    size="small"
                    type="default"
                    className={styles.actionButton}
                    onClick={handleDeleteMBFile}
                  >
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.CancelUpload" />
                  </Button>
                </div>
              )}
            </Card>
          </div>
        );
      case importFileStatus.IN_PROCESSING_QUEUE:
        return (
          <div>
            <Card>
              <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Step4" />
              <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Step4Message" />
              {fileName && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.FileName" />
                  }
                >
                  {fileName}
                </DetailItem>
              )}
              {processStatus && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.ImportStatus" />
                  }
                >
                  {processStatus}
                </DetailItem>
              )}
              {processingMsg && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.ProcessingMessage" />
                  }
                >
                  {processingMsg}
                </DetailItem>
              )}
              {uploadId && (
                <Button
                  size="small"
                  type="default"
                  className={styles.actionButton}
                  onClick={handleDeleteMBFile}
                >
                  <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.CancelUpload" />
                </Button>
              )}
            </Card>
          </div>
        );

      case importFileStatus.PROCESSING:
        return (
          <div>
            <Card>
              <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Step5" />
              <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Step5Message" />
              {fileName && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.FileName" />
                  }
                >
                  {fileName}
                </DetailItem>
              )}
              {processStatus && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.ImportStatus" />
                  }
                >
                  {processStatus}
                </DetailItem>
              )}
              {processingMsg && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.ProcessingMessage" />
                  }
                >
                  {processingMsg}
                </DetailItem>
              )}
              {uploadId && (
                <Button
                  size="small"
                  type="default"
                  className={styles.actionButton}
                  onClick={handleDeleteMBFile}
                >
                  <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.CancelUpload" />
                </Button>
              )}
            </Card>
          </div>
        );

      case importFileStatus.PROCESSING_DONE:
        return (
          <div>
            <Card>
              <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Step6" />
              <FormattedHTMLMessage id="councilUnits.CouncilUnitsPage.CouncilImportFile.Step6Message" />
              {fileName && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.FileName" />
                  }
                >
                  {fileName}
                </DetailItem>
              )}
              {processStatus && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.ImportStatus" />
                  }
                >
                  {processStatus}
                </DetailItem>
              )}
              {processingMsg && (
                <DetailItem
                  className={styles.detailItem}
                  label={
                    <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.ProcessingMessage" />
                  }
                >
                  {processingMsg}
                </DetailItem>
              )}
              {uploadId && (
                <Button
                  size="small"
                  type="default"
                  className={styles.actionButton}
                  onClick={handleDeleteMBFile}
                >
                  <FormattedMessage id="councilUnits.CouncilUnitsPage.UploadMBR.Finish" />
                </Button>
              )}
            </Card>
          </div>
        );

      default:
        return (
          <div>
            <Card>
              <FormattedMessage id="councilUnits.CouncilUnitsPage.CouncilImportFileStatus.NoImportFile" />
              {fileName}
            </Card>
          </div>
        );
    }
  };

  return (
    <div>
      <Spin spinning={loading} size="large" delay={200}>
        {renderDisplay()}
      </Spin>
    </div>
  );
}

export default CouncilmportFileStatus;
